import styled, { css, keyframes } from 'styled-components';
import { COLORS } from 'modules/core/theme/colors';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const fadeOut = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(-8px); }
`;

export const BannerWrapper = styled.div<{ $isExiting?: boolean }>`
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 9999;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  border-radius: 8px;
  background: linear-gradient(135deg, ${COLORS.BLUE_5} 0%, ${COLORS.BLUE_6} 100%);
  color: ${COLORS.WHITE};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  animation: ${({ $isExiting }) =>
    $isExiting
      ? css`
          ${fadeOut} 0.3s ease-in forwards
        `
      : css`
          ${fadeIn} 0.4s ease-out
        `};
`;

export const BannerIcon = styled.span`
  font-size: 20px;
`;

export const BannerContent = styled.div`
  display: flex;
  flex-direction: column;
`;

export const BannerTitle = styled.span`
  font-weight: 700;
  font-size: 14px;
`;

export const BannerDescription = styled.span`
  font-size: 12px;
  opacity: 0.9;
`;

export const PanelWrapper = styled.div`
  margin-top: 16px;
  background-color: ${COLORS.WHITE};
  border-radius: 5px;
  padding: 16px;
`;

export const PanelTitle = styled.h6`
  font-weight: 700;
  font-size: 14px;
  color: ${COLORS.BLUE_5};
  margin-bottom: 12px;
`;

export const ProgressCardWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 16px;
  background: ${COLORS.WHITE};
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  overflow: hidden;
  border-radius: calc(0.375rem - 1px);
`;

export const ProgressCardTitle = styled.h4<{ $small?: boolean; $fontSize?: string }>`
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: ${({ $fontSize, $small }) => $fontSize ?? ($small ? '14px' : '15px')};
  margin: 0 0 ${({ $small }) => ($small ? '0px' : '14px')} 0;
  text-align: ${({ $small }) => ($small ? 'left' : 'center')};
  letter-spacing: -0.02em;
  line-height: 1.3;
  text-transform: capitalize;
`;

export const ProgressCardGaugeWrapper = styled.div<{ $compact?: boolean; $gaugeSize?: number }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ $compact }) => ($compact ? '0px' : '16px')};
  ${({ $gaugeSize }) =>
    $gaugeSize != null
      ? css`
          width: ${$gaugeSize}px;
          height: ${$gaugeSize}px;
          min-width: ${$gaugeSize}px;
          min-height: ${$gaugeSize}px;
          flex-shrink: 0;
          box-sizing: border-box;
        `
      : ''}
`;

export const ProgressCardGaugeText = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const ProgressCardPoints = styled.span<{
  $small?: boolean;
  $fontSize?: string;
  $margin?: string;
}>`
  font-family: 'Poppins', sans-serif;
  font-weight: ${({ $small }) => ($small ? 400 : 700)};
  font-size: ${({ $fontSize, $small }) => $fontSize ?? ($small ? '14px' : '25px')};
  color: ${COLORS.BLACK};
  line-height: 1;
  letter-spacing: -0.02em;
  margin: ${({ $margin }) => $margin ?? '0 6px'};
`;

export const ProgressCardPointsLabel = styled.span`
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 11px;
  color: ${COLORS.BLACK};
  text-transform: capitalize;
  margin-top: 2px;
`;

export const ProgressCardSubtitle = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  text-align: center;
  line-height: 1.4;
  text-transform: capitalize;
  margin: 8px 0 0;
`;

export const ProgressCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: auto;
  margin-top: auto;
`;

export const ProgressCardReward = styled.div`
  margin-top: auto;
  width: 100%;
  text-align: center;
  z-index: 1;
`;

export const RewardButton = styled.a<{
  $disabled?: boolean;
  $btnFontSize?: string;
  $padding?: string;
  $margin?: string;
}>`
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  gap: 5px;
  padding: ${({ $padding }) => $padding ?? '5px 10px'};
  border-radius: 4px;
  background: ${({ $disabled }) => ($disabled ? COLORS.GREY_34 : 'linear-gradient(90deg, #0064e2 0%, #21ccf2 100%)')};
  color: ${({ $disabled }) => ($disabled ? '#5e6a71' : COLORS.WHITE)};
  font-family: 'Poppins', sans-serif;
  font-size: ${({ $btnFontSize }) => $btnFontSize ?? '10px'};
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  transition: opacity 0.15s ease;
  box-shadow: ${({ $disabled }) => ($disabled ? 'none' : '0 4px 16px rgba(0, 0, 0, 0.15)')};
  margin: ${({ $margin }) => $margin ?? 0};

  &:hover {
    opacity: 0.85;
    color: ${({ $disabled }) => ($disabled ? '#5e6a71' : COLORS.WHITE)};
    text-decoration: none;
  }
`;

export const RewardButtonIcon = styled.img<{ $disabled?: boolean; $iconHeight?: string }>`
  height: ${({ $iconHeight }) => $iconHeight ?? '13px'};
  object-fit: contain;
  flex-shrink: 0;
  filter: ${({ $disabled }) => ($disabled ? 'grayscale(1) brightness(0.65)' : 'brightness(0) invert(1)')};
`;

export const ProgressCardsColumn = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

export const WavesDecoration = styled.img`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  pointer-events: none;
  user-select: none;
  height: 55px;
  object-fit: cover;
`;

export const ProgressCardRow = styled.div<{ $gap?: string; $padding?: string }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex: 1;
  padding: ${({ $padding }) => $padding ?? '10px 16px'};
  gap: ${({ $gap }) => $gap ?? '14px'};
  min-height: 0;
`;

export const ProgressCardRowInfo = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  min-width: 0;
`;

export const ProgressCardRowDivider = styled.div`
  height: 1px;
  background-color: ${COLORS.BLUE_17};
  margin: 0 16px;
`;
